import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Row, Col } from "react-bootstrap";
import moment from "moment";
import { orderBy } from "lodash";

import * as projectActions from "../../../actions/projectActions";
import * as exportActions from "../../../actions/exportActions";
import * as reportActions from "../../../actions/reportActions";
import * as departmentActions from "../../../actions/departmentActions";

import Loading from "../../common/Loading";
import { DropdownSearchSelect } from "../../common/DropdownSearch";

import TopProjects from "./TopProjects";
import DepartmentTimeUsage from "./DepartmentTimeUsage";
import TaskBreakdown from "./TaskBreakdown";
import ActiveProjectsByTeam from "./ActiveProjectsByTeam";
import ProjectSummary from "./ProjectSummary";
import InformationalTooltip from "./InformationalTooltip";
import UserResourceSummary from "../../user/UserResourceSummary";

import ReportFilters from "../ReportFiltersComponent";

const TOP_PROJECTS = 5;

const WEEKS = ((back) => {
  const week = moment().startOf("week").add(1, "day");

  return Array.from({ length: back }, (v, i) =>
    week.clone().subtract(i, "weeks")
  ).map((v) => ({
    label: v.format("MM/DD/YYYY"),
    value: v
  }));
})(52);

const INITIAL_FILTERS_STATE = {
  week: WEEKS[0],
  department: {}
};

const DepartmentProjectSummary = ({
  isMobile,
  actions,
  report,
  loading,
  auth,
  projects,
  exporting,
  exported,
  resource_summary_report,
  departments
}) => {
  const [filters, setFilters] = useState(INITIAL_FILTERS_STATE);

  const handleChangeFilters = (field, input) => {
    setFilters((prev) => ({
      ...prev,
      [field]: input
    }));
  };

  const getCurrentUserDepartment = useCallback(() => {
    if (auth.user?.department) {
      const { id, description } = auth.user.department;
      return { label: description, value: id };
    }
    return [];
  }, [auth.user]);

  const resetFilters = () => {
    setFilters({
      ...INITIAL_FILTERS_STATE,
      department: getCurrentUserDepartment()
    });
  };

  useEffect(() => {
    actions.loadDepartments();
  }, [actions]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      department: getCurrentUserDepartment()
    }));
  }, [getCurrentUserDepartment]);

  const fetchData = () => {
    if (filters.department.value) {
      actions.loadDepartmentProjects({
        filtered: {
          department: filters.department.value,
          week: filters.week.value.toLocaleString(),
          top: TOP_PROJECTS
        }
      });
      actions.loadProjectSummary({
        filtered: {
          department: filters.department.value,
          week: filters.week.value.toLocaleString()
        }
      });
      actions.getResourceSummary({
        filtered: {
          department: filters.department.value,
          week: filters.week.value.toLocaleString(),
          top: TOP_PROJECTS
        }
      });
    }
    actions.clearProjects();
    actions.clearResourceSummary();
  };

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = "Practice-Summary.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions]);

  const handleExportClick = () => {
    const payload = {
      data: {
        department: filters.department.value,
        week: filters.week.value.toLocaleString()
      },
      type: "DEPARTMENT_PROJECT_SUMMARY",
      format: "pdf"
    };
    actions.performExport(payload);
  };

  const departmentOptions = useMemo(() => {
    const options = departments.map((dep) => ({
      value: dep._id,
      label: dep.description
    }));
    return orderBy(options, [(option) => option.label.toLowerCase()]);
  }, [departments]);

  return (
    <React.Fragment>
      <ReportFilters
        onFetchClick={fetchData}
        onResetClick={resetFilters}
        title="Practice Summary Report"
        loading={loading}
        pdfExportAvailable
        onPDFExport={handleExportClick}
        exportDisabled={loading || exporting || !report.topProjects?.length}
      >
        <Row>
          <Col md={3}>
            <DropdownSearchSelect
              label
              fieldLabel="Department *"
              placeholder="Department"
              name="department"
              onChange={handleChangeFilters}
              value={filters.department}
              data={departmentOptions}
            />
          </Col>
          <Col md={3}>
            <DropdownSearchSelect
              label
              fieldLabel="Week Of *"
              isSearchable={false}
              placeholder="Week"
              name="week"
              onChange={handleChangeFilters}
              value={filters.week}
              data={WEEKS}
            />
          </Col>
        </Row>
        <Row>
          <Col md={3}>
            <label
              className="control-label"
              style={{ textTransform: "uppercase" }}
            >
              * Required Fields
            </label>
          </Col>
        </Row>
      </ReportFilters>

      {loading && (
        <Row style={{ display: "flex", justifyContent: "center" }}>
          <Loading />
        </Row>
      )}

      {!loading && !!report.topProjects?.length && (
        <div>
          <div className="dps-container-top">
            <TopProjects
              projects={report.topProjects}
              topAmount={TOP_PROJECTS}
              dark={auth.isNightMode}
            />
            <DepartmentTimeUsage
              calls={report.calls}
              totals={report.totals}
              dark={auth.isNightMode}
            />
          </div>
          {
            // Hidden temporally
            false && (
              <div className="dps-container-middle">
                <TaskBreakdown calls={report.calls} dark={auth.isNightMode} />
              </div>
            )
          }
          <div className="dps-container-bottom">
            {resource_summary_report.length > 0 && (
              <div className="header-section-container">
                <h1 className="resource-summary-title">Resource Summaries</h1>
                <InformationalTooltip
                  id="department_project_summary"
                  dark={auth.isNightMode}
                  content="Employeee breakdown of hours top projects"
                />
              </div>
            )}
            <div className="resource-summary-container">
              {resource_summary_report.length > 0 &&
                resource_summary_report.map((item) => (
                  <UserResourceSummary
                    key={item._id}
                    isNightMode={auth.isNightMode}
                    resource_summary={item}
                  />
                ))}
            </div>
            <ActiveProjectsByTeam
              calls={report.calls}
              departmentCategories={report.departmentCategories}
              dark={auth.isNightMode}
            />
            <ProjectSummary
              projects={projects}
              isMobile={isMobile}
              loading={loading}
              dark={auth.isNightMode}
            />
          </div>
        </div>
      )}

      {!loading && !report.topProjects?.length && (
        <div className="no-data">No Data Available</div>
      )}
    </React.Fragment>
  );
};

DepartmentProjectSummary.propTypes = {
  actions: PropTypes.object,
  isMobile: PropTypes.bool,
  report: PropTypes.object,
  loading: PropTypes.bool,
  auth: PropTypes.object,
  projects: PropTypes.array,
  exporting: PropTypes.bool,
  exported: PropTypes.string,
  resource_summary_report: PropTypes.array,
  departments: PropTypes.array
};

DepartmentProjectSummary.defaultProps = {
  report: {},
  loading: false,
  auth: {},
  projects: [],
  exporting: false,
  exported: null
};

function mapStatesToProps(state) {
  return {
    report: state.reducers.DPSReport,
    loading: state.reducers.loading,
    auth: state.reducers.auth,
    projects: state.reducers.projects,
    exporting: state.reducers.exporting,
    exported: state.reducers.exported,
    resource_summary_report: state.reducers.resource_summary_report,
    departments: state.reducers.departments
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...projectActions,
        ...exportActions,
        ...reportActions,
        ...departmentActions
      },

      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(DepartmentProjectSummary);
